import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Lock, LogOut, RefreshCw } from "lucide-react";
import Atmosphere from "@/components/silver/layout/Atmosphere";
import JourneyDialog from "@/components/insights/JourneyDialog";
import { AudiencePanel, Card, DailyChart, EngagementPanel, EnquiriesPanel, Funnel, FormPanel, GeoPanel, Kpis, BarList, SessionsTable } from "@/components/insights/Panels";
import { formatApiError } from "@/lib/submitSilverCampaign";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "nc_insights_token";
const RANGES = [[7, "7 days"], [30, "30 days"], [90, "90 days"]];
const FILTERS = [["all", "All visits"], ["built", "Built an audience"], ["dropped", "Built, no enquiry"], ["enquired", "Sent an enquiry"]];

function Gate({ onUnlock }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await axios.post(`${API}/analytics/unlock`, { password: pw });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      onUnlock(data.token);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <div className="relative grid min-h-screen place-items-center px-5">
      <form onSubmit={submit} className="glass-strong w-full max-w-sm p-8" data-testid="insights-gate-form">
        <span className="grid h-11 w-11 place-items-center rounded-full bg-[#96C11F]/15 text-[#C6EE5A]"><Lock className="h-5 w-5" /></span>
        <h1 className="mt-5 text-2xl font-extrabold tracking-[-0.03em] text-white">Audience insights</h1>
        <p className="mt-1.5 text-[13px] text-white/55">Enter the password to continue.</p>
        <input type="password" autoFocus value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password" autoComplete="current-password" className="field mt-6 w-full px-4 py-3 text-[14px]" data-testid="insights-password-input" />
        {err && <p className="mt-2 text-[12px] text-[#FCA5A5]" data-testid="insights-gate-error">{err}</p>}
        <button type="submit" disabled={busy || !pw} className="btn-primary mt-5 w-full !py-3.5 disabled:opacity-60" data-testid="insights-unlock-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Unlock"}
        </button>
        <Link to="/" className="mt-5 inline-flex items-center gap-1.5 text-[12px] text-white/45 hover:text-white"><ArrowLeft className="h-3.5 w-3.5" /> Back to site</Link>
      </form>
    </div>
  );
}

function Dashboard({ token, onExpire }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [rows, setRows] = useState({ total: 0, sessions: [] });
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, s] = await Promise.all([
        axios.get(`${API}/analytics/overview`, { params: { days }, headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/analytics/sessions`, { params: { days, status: filter, limit: 50 }, headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setData(o.data);
      setRows(s.data);
    } catch (e) {
      if (e.response?.status === 401) onExpire();
    } finally { setLoading(false); }
  }, [days, filter, token, onExpire]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="grid min-h-screen place-items-center"><Loader2 className="h-7 w-7 animate-spin text-[#C6EE5A]" /></div>;
  return (
    <div className="relative mx-auto max-w-7xl space-y-6 px-5 pb-24 pt-8 sm:px-8" data-testid="insights-dashboard">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <Link to="/" className="inline-flex items-center gap-1.5 text-[12px] text-white/45 hover:text-white"><ArrowLeft className="h-3.5 w-3.5" /> Back to site</Link>
          <h1 className="mt-2 text-3xl font-extrabold tracking-[-0.04em] text-white sm:text-4xl">Audience insights</h1>
          <p className="mt-1 text-[13px] text-white/50">How people find, use and leave the site and the audience builder.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {RANGES.map(([d, l]) => (
            <button key={d} type="button" onClick={() => setDays(d)} className={cn("rounded-full border px-4 py-2 text-[12px] font-semibold transition-colors", days === d ? "border-[#96C11F] bg-[#96C11F] text-[#12290F]" : "border-white/15 text-white/70 hover:bg-white/10")} data-testid={`insights-range-${d}`}>{l}</button>
          ))}
          <button type="button" onClick={load} className="grid h-9 w-9 place-items-center rounded-full border border-white/15 text-white/70 hover:bg-white/10" aria-label="Refresh" data-testid="insights-refresh-btn"><RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} /></button>
          <button type="button" onClick={onExpire} className="grid h-9 w-9 place-items-center rounded-full border border-white/15 text-white/70 hover:bg-white/10" aria-label="Lock" data-testid="insights-lock-btn"><LogOut className="h-4 w-4" /></button>
        </div>
      </header>

      <Kpis k={data.kpis} enquiries={data.enquiries} />
      <Card title="Where people drop off" hint="Sessions that reached each step. Red shows how many left between steps." testId="insights-funnel-card">
        {data.kpis.sessions ? <Funnel steps={data.funnel} /> : <p className="py-6 text-center text-[13px] text-white/40">No visits recorded yet. Visits appear here once visitors accept analytics on the site.</p>}
      </Card>
      <Card title="Visits over time" hint="Sessions, audiences built and enquiries per day."><DailyChart data={data.daily} /></Card>
      <Card title="Audience choices" hint="What visitors search for and build." testId="insights-audience-card"><AudiencePanel a={data.audience} /></Card>
      <Card title="The enquiry form" hint="Where people stop when filling in the brief." testId="insights-form-card"><FormPanel f={data.form} /></Card>
      <Card title="Time and engagement" hint="Active time per section, scroll depth and clicks." testId="insights-engagement-card"><EngagementPanel e={data.engagement} /></Card>
      <Card title="Where visitors are" hint="Approximate location from the connection; the IP address is not stored." testId="insights-geo-card"><GeoPanel geo={data.geo} /></Card>
      <div className="grid gap-6 md:grid-cols-3">
        <Card title="Traffic sources"><BarList items={data.traffic.referrers} /></Card>
        <Card title="Devices"><BarList items={data.traffic.devices} color="#C6EE5A" /></Card>
        <Card title="New vs returning"><BarList items={data.traffic.newVsReturning} color="#7AA818" /></Card>
      </div>
      <Card title="Enquiries received" hint="Linked to the visit that produced them." testId="insights-enquiries-card"><EnquiriesPanel e={data.enquiries} onOpen={setOpen} /></Card>
      <Card title="Visitor journeys" hint={`${rows.total} visit${rows.total === 1 ? "" : "s"} · click a row to replay the journey.`} testId="insights-journeys-card">
        <div className="mb-5 flex flex-wrap gap-2">
          {FILTERS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setFilter(k)} className={cn("rounded-full border px-3.5 py-1.5 text-[12px] font-semibold transition-colors", filter === k ? "border-[#96C11F] bg-[#96C11F]/20 text-[#C6EE5A]" : "border-white/15 text-white/60 hover:bg-white/10")} data-testid={`journey-filter-${k}`}>{l}</button>
          ))}
        </div>
        <SessionsTable rows={rows.sessions} onOpen={setOpen} />
      </Card>
      <JourneyDialog sessionId={open} token={token} onClose={() => setOpen(null)} />
    </div>
  );
}

export default function Insights() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const lock = useCallback(() => { sessionStorage.removeItem(TOKEN_KEY); setToken(null); }, []);
  return (
    <div className="relative min-h-screen text-white">
      <Atmosphere />
      {token ? <Dashboard token={token} onExpire={lock} /> : <Gate onUnlock={setToken} />}
    </div>
  );
}
